import { useState, useRef } from "react";
import {
  DndContext,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  Plus,
  Play,
  Save,
  GitBranch,
  Bot,
  Sparkles,
  FileOutput,
  X,
  ChevronDown,
  RefreshCw,
  Wrench,
  BookOpen,
  Split,
  Merge,
  ShieldCheck,
  Target,
  Gauge,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { Workflow, WorkflowNode, WorkflowEdge } from "@/types/aiToolkit";
import { AGENTS } from "@/data/aiToolkit";
import { useWorkflowRun } from "@/hooks/useWorkflowRun";

interface Position {
  x: number;
  y: number;
}

interface WorkflowBuilderProps {
  workflow?: Workflow;
  onSave?: (workflow: Partial<Workflow>) => void;
  onRun?: (workflow: Partial<Workflow>) => void;
  onPublish?: (workflow: Partial<Workflow>, changelog?: string) => void;
}

const NODE_WIDTH = 200;
const NODE_HEIGHT = 80;

const NODE_TYPE_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  trigger: { icon: Play, color: "bg-green-600", label: "Trigger" },
  agent: { icon: Bot, color: "bg-purple-600", label: "Agent" },
  prompt: { icon: Sparkles, color: "bg-blue-600", label: "Prompt" },
  condition: { icon: GitBranch, color: "bg-yellow-600", label: "Condition" },
  output: { icon: FileOutput, color: "bg-orange-600", label: "Output" },
  transform: { icon: RefreshCw, color: "bg-cyan-600", label: "Transform" },
  tool: { icon: Wrench, color: "bg-indigo-600", label: "Tool" },
  knowledge: { icon: BookOpen, color: "bg-teal-600", label: "Knowledge" },
  parallel: { icon: Split, color: "bg-pink-600", label: "Parallel" },
  merge: { icon: Merge, color: "bg-violet-600", label: "Merge" },
  validation: { icon: ShieldCheck, color: "bg-amber-600", label: "Validation" },
  "entity-resolution": { icon: Target, color: "bg-slate-600", label: "Entity Resolution" },
  "confidence-gate": { icon: Gauge, color: "bg-emerald-600", label: "Confidence Gate" },
  "quality-gate": { icon: ShieldCheck, color: "bg-rose-600", label: "Quality Gate" },
};

function DraggableNode({ 
  node, 
  isSelected,
  onSelect,
  onDelete,
  onConnect,
}: { 
  node: WorkflowNode; 
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onConnect: (nodeId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: node.id,
    data: { type: 'node', node },
  });
  
  const config = NODE_TYPE_CONFIG[node.type] || NODE_TYPE_CONFIG.agent;
  const Icon = config.icon;

  const style = {
    position: 'absolute' as const,
    left: node.position.x,
    top: node.position.y,
    width: NODE_WIDTH,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: isDragging ? 100 : isSelected ? 50 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cn(
        "p-3 rounded-lg border-2 bg-card shadow-sm cursor-grab active:cursor-grabbing transition-all",
        isSelected ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/50"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      data-testid={`workflow-node-${node.id}`}
    >
      <div className="flex items-start gap-2">
        <div className={cn("p-1.5 rounded-md", config.color)}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{node.data.label}</p>
          <p className="text-[10px] text-muted-foreground truncate">{node.data.description || config.label}</p>
        </div>
        {isSelected && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 -mt-1 -mr-1 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            data-testid={`button-delete-node-${node.id}`}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      
      {/* Connection points */}
      {node.type !== 'output' && (
        <div 
          className="absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-primary border-2 border-background cursor-crosshair hover:scale-125 transition-transform"
          onClick={(e) => {
            e.stopPropagation();
            onConnect(node.id);
          }}
          data-testid={`connection-output-${node.id}`}
        />
      )}
      {node.type !== 'trigger' && (
        <div 
          className="absolute -left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-muted border-2 border-border"
          data-testid={`connection-input-${node.id}`}
        />
      )}
    </div>
  );
}

function EdgeLine({ 
  sourceNode, 
  targetNode,
  isSelected,
  onSelect,
  onDelete,
}: { 
  sourceNode: WorkflowNode;
  targetNode: WorkflowNode;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const startX = sourceNode.position.x + NODE_WIDTH;
  const startY = sourceNode.position.y + NODE_HEIGHT / 2;
  const endX = targetNode.position.x;
  const endY = targetNode.position.y + NODE_HEIGHT / 2;
  
  const controlPointOffset = Math.min(Math.abs(endX - startX) / 2, 100);
  
  const d = `M ${startX} ${startY} C ${startX + controlPointOffset} ${startY}, ${endX - controlPointOffset} ${endY}, ${endX} ${endY}`;
  
  return (
    <g onClick={onSelect} className="cursor-pointer group">
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
      />
      <path
        d={d}
        fill="none"
        stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--border))"}
        strokeWidth={isSelected ? 3 : 2}
        className="transition-all group-hover:stroke-primary"
        markerEnd="url(#arrowhead)"
      />
      {isSelected && (
        <foreignObject x={(startX + endX) / 2 - 10} y={(startY + endY) / 2 - 10} width={20} height={20}>
          <Button
            variant="destructive"
            size="icon"
            className="h-5 w-5"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </foreignObject>
      )}
    </g>
  );
}

export function WorkflowBuilder({ workflow, onSave, onRun, onPublish }: WorkflowBuilderProps) {
  const [nodes, setNodes] = useState<WorkflowNode[]>(workflow?.nodes || []);
  const [edges, setEdges] = useState<WorkflowEdge[]>(workflow?.edges || []);
  const [workflowName, setWorkflowName] = useState(workflow?.name || "New Workflow");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [showAddNodeDialog, setShowAddNodeDialog] = useState(false);
  const [addNodePosition, setAddNodePosition] = useState<Position>({ x: 100, y: 100 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const { publishWorkflow } = useWorkflowRun();
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    
    if (active.data.current?.type === 'node') {
      setNodes(nodes.map(node => {
        if (node.id === active.id) {
          return {
            ...node,
            position: {
              x: Math.max(0, node.position.x + delta.x),
              y: Math.max(0, node.position.y + delta.y),
            },
          };
        }
        return node;
      }));
    }
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setConnectingFrom(null);
    }
  };

  const handleCanvasDoubleClick = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setAddNodePosition({
        x: e.clientX - rect.left - NODE_WIDTH / 2,
        y: e.clientY - rect.top - NODE_HEIGHT / 2,
      });
      setShowAddNodeDialog(true);
    }
  };

  const handleConnect = (nodeId: string) => {
    if (connectingFrom === null) {
      setConnectingFrom(nodeId);
    } else if (connectingFrom !== nodeId) {
      const existingEdge = edges.find(e => e.source === connectingFrom && e.target === nodeId);
      if (!existingEdge) {
        const newEdge: WorkflowEdge = {
          id: `e-${connectingFrom}-${nodeId}`,
          source: connectingFrom,
          target: nodeId,
        };
        setEdges([...edges, newEdge]);
      }
      setConnectingFrom(null);
    } else {
      setConnectingFrom(null);
    }
  };

  const addNode = (type: string, data: any) => {
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}`,
      type: type as any,
      position: addNodePosition,
      data: {
        label: data.label || `New ${NODE_TYPE_CONFIG[type]?.label || 'Node'}`,
        description: data.description || '',
        ...data,
      },
    };
    setNodes([...nodes, newNode]);
    setShowAddNodeDialog(false);
  };

  const deleteNode = (nodeId: string) => {
    setNodes(nodes.filter(n => n.id !== nodeId));
    setEdges(edges.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNodeId(null);
  };

  const deleteEdge = (edgeId: string) => {
    setEdges(edges.filter(e => e.id !== edgeId));
    setSelectedEdgeId(null);
  };

  const handleSave = () => {
    const workflowData: Partial<Workflow> = {
      id: workflow?.id || `workflow-${Date.now()}`,
      name: workflowName,
      nodes,
      edges,
      trigger: { type: 'manual', config: {} },
    };
    onSave?.(workflowData);
  };

  const handleRun = () => {
    const workflowData: Partial<Workflow> = {
      id: workflow?.id || `workflow-${Date.now()}`,
      name: workflowName,
      nodes,
      edges,
      trigger: workflow?.trigger ?? { type: 'manual', config: {} },
      entryNodeId: nodes.find((n) => n.type === "trigger")?.id ?? nodes[0]?.id,
    };
    onRun?.(workflowData);
  };

  const handlePublish = async () => {
    const workflowData: Partial<Workflow> = {
      id: workflow?.id || `workflow-${Date.now()}`,
      name: workflowName,
      nodes,
      edges,
      trigger: workflow?.trigger ?? { type: 'manual', config: {} },
      entryNodeId: nodes.find((n) => n.type === "trigger")?.id ?? nodes[0]?.id,
    };
    if (onPublish) {
      onPublish(workflowData);
      return;
    }
    await publishWorkflow(workflowData as Workflow, "Published from builder");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Input
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            className="h-8 w-48 text-sm font-medium"
            data-testid="input-workflow-name"
          />
          <Badge variant="secondary" className="text-[10px]">
            {nodes.length} nodes
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="button-add-node">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Node
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => {
                setAddNodePosition({ x: 100, y: nodes.length * 100 + 50 });
                addNode('trigger', { label: 'Start' });
              }}>
                <Play className="h-4 w-4 mr-2 text-green-600" />
                Trigger
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setAddNodePosition({ x: 300, y: nodes.length * 100 + 50 });
                setShowAddNodeDialog(true);
              }}>
                <Bot className="h-4 w-4 mr-2 text-purple-600" />
                Agent
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setAddNodePosition({ x: 300, y: nodes.length * 100 + 50 });
                addNode('prompt', { label: 'Prompt' });
              }}>
                <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
                Prompt
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => {
                setAddNodePosition({ x: 300, y: nodes.length * 100 + 50 });
                addNode('condition', { label: 'Condition' });
              }}>
                <GitBranch className="h-4 w-4 mr-2 text-yellow-600" />
                Condition
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setAddNodePosition({ x: 500, y: nodes.length * 100 + 50 });
                addNode('output', { label: 'Output' });
              }}>
                <FileOutput className="h-4 w-4 mr-2 text-orange-600" />
                Output
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => addNode('tool', { label: 'Tool', toolId: 'clickup', toolOperation: 'create_task' })}>
                <Wrench className="h-4 w-4 mr-2 text-indigo-600" />
                Tool
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('knowledge', { label: 'Load Context', knowledgeSource: 'trigger' })}>
                <BookOpen className="h-4 w-4 mr-2 text-teal-600" />
                Knowledge
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('parallel', { label: 'Parallel Fork' })}>
                <Split className="h-4 w-4 mr-2 text-pink-600" />
                Parallel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('merge', { label: 'Merge', mergeStrategy: 'all' })}>
                <Merge className="h-4 w-4 mr-2 text-violet-600" />
                Merge
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('validation', { label: 'Validation' })}>
                <ShieldCheck className="h-4 w-4 mr-2 text-amber-600" />
                Validation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('confidence-gate', { label: 'Confidence Gate', confidenceThreshold: 0.8 })}>
                <Gauge className="h-4 w-4 mr-2 text-emerald-600" />
                Confidence Gate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('quality-gate', { label: 'Quality Gate', qualityThreshold: 0.5 })}>
                <ShieldCheck className="h-4 w-4 mr-2 text-rose-600" />
                Quality Gate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addNode('entity-resolution', { label: 'Entity Resolution' })}>
                <Target className="h-4 w-4 mr-2 text-slate-600" />
                Entity Resolution
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="outline" size="sm" className="h-8" onClick={handleSave} data-testid="button-save-workflow">
            <Save className="h-3.5 w-3.5 mr-1" />
            Save
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => void handlePublish()} data-testid="button-publish-workflow">
            <Upload className="h-3.5 w-3.5 mr-1" />
            Publish
          </Button>
          <Button size="sm" className="h-8" onClick={handleRun} data-testid="button-run-workflow">
            <Play className="h-3.5 w-3.5 mr-1" />
            Run
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden bg-[radial-gradient(circle,hsl(var(--border))_1px,transparent_1px)] bg-[length:20px_20px]">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div
            ref={canvasRef}
            className="absolute inset-0 overflow-auto"
            onClick={handleCanvasClick}
            onDoubleClick={handleCanvasDoubleClick}
          >
            {/* SVG layer for edges */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ minWidth: '2000px', minHeight: '1500px' }}>
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="hsl(var(--border))"
                  />
                </marker>
              </defs>
              <g className="pointer-events-auto">
                {edges.map(edge => {
                  const sourceNode = nodes.find(n => n.id === edge.source);
                  const targetNode = nodes.find(n => n.id === edge.target);
                  if (!sourceNode || !targetNode) return null;
                  return (
                    <EdgeLine
                      key={edge.id}
                      sourceNode={sourceNode}
                      targetNode={targetNode}
                      isSelected={selectedEdgeId === edge.id}
                      onSelect={() => {
                        setSelectedEdgeId(edge.id);
                        setSelectedNodeId(null);
                      }}
                      onDelete={() => deleteEdge(edge.id)}
                    />
                  );
                })}
              </g>
            </svg>

            {/* Nodes layer */}
            <div className="relative" style={{ minWidth: '2000px', minHeight: '1500px' }}>
              {nodes.map(node => (
                <DraggableNode
                  key={node.id}
                  node={node}
                  isSelected={selectedNodeId === node.id}
                  onSelect={() => {
                    setSelectedNodeId(node.id);
                    setSelectedEdgeId(null);
                  }}
                  onDelete={() => deleteNode(node.id)}
                  onConnect={handleConnect}
                />
              ))}
            </div>
          </div>
        </DndContext>

        {/* Helper text */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-muted-foreground">
              <GitBranch className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No nodes yet</p>
              <p className="text-xs mt-1">Double-click to add a node or use the toolbar above</p>
            </div>
          </div>
        )}
        
        {connectingFrom && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
            Click another node to connect, or click empty space to cancel
          </div>
        )}
      </div>

      {/* Add Node Dialog */}
      <Dialog open={showAddNodeDialog} onOpenChange={setShowAddNodeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Agent Node</DialogTitle>
            <DialogDescription>Select an agent to add to your workflow</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-2 pr-4">
              {AGENTS.map(agent => (
                <button
                  key={agent.id}
                  className="w-full flex items-start gap-3 p-3 rounded-md border hover:bg-muted/50 text-left transition-colors"
                  onClick={() => addNode('agent', { 
                    label: agent.name, 
                    description: agent.description,
                    agentId: agent.id,
                  })}
                  data-testid={`button-add-agent-${agent.id}`}
                >
                  <div className={cn("p-2 rounded-md flex-shrink-0", agent.color)}>
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{agent.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{agent.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
